import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Stack,
  CircularProgress,
  Avatar,
  useTheme,
  Fade,
} from '@mui/material';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AOS from 'aos';
import 'aos/dist/aos.css';
import { getApiUrl } from '../config/api';

const AI_AVATAR = '/Monogram_72_ppi-01.png';
const USER_AVATAR = 'https://randomuser.me/api/portraits/lego/1.jpg';

const ChatDemo = () => {
  const [messages, setMessages] = useState([
    {
      sender: 'ai',
      text: "Hi! I'm Bixbite AI. Describe the software you want to build, and I'll help you get started!",
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const chatContainerRef = useRef(null);
  const theme = useTheme();

  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
      disable: window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    });
  }, []);

  useEffect(() => {
    if (chatContainerRef.current) {
      chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
    }
  }, [messages, loading]);

  const handleSend = async () => {
    if (!input.trim() || loading) return;
    setErrorMsg('');
    const userMessage = { sender: 'user', text: input.trim() };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const formData = new FormData();
      formData.append('question', userMessage.text);

      const response = await fetch(getApiUrl('api/get_chat_bot_response'), {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to get response from AI.');
      }

      const data = await response.json();
      if (data && typeof data.answer === 'string') {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'ai',
            text: data.answer,
          },
        ]);
      } else {
        setMessages((prev) => [
          ...prev,
          {
            sender: 'ai',
            text: "Sorry, I couldn't process your request. Please try again.",
          },
        ]);
      }
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        {
          sender: 'ai',
          text: "Sorry, there was an error connecting to the AI. Please try again later.",
        },
      ]);
      setErrorMsg('Failed to get response from AI. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 700,
        mx: 'auto',
        minHeight: 480,
        display: 'flex',
        flexDirection: 'column',
        borderRadius: '24px',
        bgcolor: 'white',
        overflow: 'hidden',
        border: '1px solid rgba(0, 121, 107, 0.1)',
      }}
    >
      {/* Header */}
      <Box
        sx={{
          p: 3,
          background: 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            bgcolor: 'rgba(255, 255, 255, 0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backdropFilter: 'blur(10px)',
          }}
        >
          <SmartToyIcon sx={{ fontSize: 28, color: 'white' }} />
        </Box>
        <Box>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              color: 'white',
              letterSpacing: '-0.01em',
            }}
          >
            Bixbite AI Chat Demo
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: 'rgba(255, 255, 255, 0.8)',
              fontSize: '0.85rem',
            }}
          >
            Ask me anything about building software
          </Typography>
        </Box>
        <Box
          sx={{
            ml: 'auto',
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
          }}
        >
          <Box
            sx={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: '#4caf50',
              animation: 'pulse 2s ease-in-out infinite',
            }}
          />
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            Online
          </Typography>
        </Box>
      </Box>

      {/* Chat Messages */}
      <Box
        ref={chatContainerRef}
        sx={{
          flex: 1,
          overflowY: 'auto',
          px: 3,
          py: 3,
          bgcolor: '#f8fffe',
          minHeight: 300,
          maxHeight: 380,
          '&::-webkit-scrollbar': {
            width: 6,
          },
          '&::-webkit-scrollbar-track': {
            bgcolor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            bgcolor: 'rgba(0, 121, 107, 0.2)',
            borderRadius: 3,
          },
        }}
      >
        <Stack spacing={2.5}>
          {messages.map((msg, idx) => (
            <Fade in key={idx} timeout={300}>
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: msg.sender === 'user' ? 'row-reverse' : 'row',
                  alignItems: 'flex-end',
                  gap: 1.5,
                }}
              >
                <Avatar
                  src={msg.sender === 'ai' ? AI_AVATAR : USER_AVATAR}
                  alt={msg.sender === 'ai' ? 'AI' : 'User'}
                  sx={{
                    width: 40,
                    height: 40,
                    border: '2px solid',
                    borderColor: msg.sender === 'ai' 
                      ? 'rgba(0, 121, 107, 0.2)' 
                      : 'rgba(0, 77, 64, 0.2)',
                    boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.15)',
                  }}
                  imgProps={{
                    style: { objectFit: 'contain', background: 'white' },
                  }}
                />
                <Box
                  sx={{
                    bgcolor: msg.sender === 'ai' ? 'white' : 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                    background: msg.sender === 'ai' 
                      ? 'white' 
                      : 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
                    color: msg.sender === 'ai' ? '#1a2f2c' : 'white',
                    px: 3,
                    py: 2,
                    borderRadius: msg.sender === 'ai' 
                      ? '20px 20px 20px 4px' 
                      : '20px 20px 4px 20px',
                    maxWidth: '78%',
                    fontSize: '0.95rem',
                    lineHeight: 1.6,
                    boxShadow: msg.sender === 'ai'
                      ? '0 4px 16px -4px rgba(0, 77, 64, 0.1)'
                      : '0 4px 16px -4px rgba(0, 77, 64, 0.3)',
                    wordBreak: 'break-word',
                    border: msg.sender === 'ai' ? '1px solid rgba(0, 121, 107, 0.08)' : 'none',
                  }}
                >
                  {msg.text}
                </Box>
              </Box>
            </Fade>
          ))}

          {loading && (
            <Fade in>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: 1.5,
                }}
              >
                <Avatar
                  src={AI_AVATAR}
                  alt="AI"
                  sx={{
                    width: 40,
                    height: 40,
                    border: '2px solid rgba(0, 121, 107, 0.2)',
                    boxShadow: '0 4px 12px -4px rgba(0, 0, 0, 0.15)',
                  }}
                  imgProps={{
                    style: { objectFit: 'contain', background: 'white' },
                  }}
                />
                <Box
                  sx={{
                    bgcolor: 'white',
                    border: '1px solid rgba(0, 121, 107, 0.08)',
                    px: 3,
                    py: 2,
                    borderRadius: '20px 20px 20px 4px',
                    boxShadow: '0 4px 16px -4px rgba(0, 77, 64, 0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <Box sx={{ display: 'flex', gap: 0.5 }}>
                    {[0, 1, 2].map((i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: '#00796b',
                          animation: 'pulse 1.5s ease-in-out infinite',
                          animationDelay: `${i * 0.2}s`,
                        }}
                      />
                    ))}
                  </Box>
                  <Typography variant="body2" sx={{ color: theme.palette.text.secondary, ml: 0.5 }}>
                    Thinking...
                  </Typography>
                </Box>
              </Box>
            </Fade>
          )}
        </Stack>
      </Box>

      {/* Error Message */}
      {errorMsg && (
        <Typography
          variant="body2"
          sx={{
            color: 'error.main',
            textAlign: 'center',
            py: 1,
            px: 3,
            bgcolor: 'rgba(198, 40, 40, 0.05)',
            fontWeight: 500,
          }}
        >
          {errorMsg}
        </Typography>
      )}

      {/* Input Area */}
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          handleSend();
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 3,
          pt: 2,
          bgcolor: 'white',
          borderTop: '1px solid rgba(0, 121, 107, 0.08)',
        }}
        autoComplete="off"
      >
        <TextField
          fullWidth
          variant="outlined"
          placeholder="Type your message..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleInputKeyDown}
          disabled={loading}
          size="small"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '14px',
              bgcolor: '#f8fffe',
              transition: 'all 0.3s ease',
              '& fieldset': {
                borderColor: 'rgba(0, 121, 107, 0.15)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(0, 121, 107, 0.3)',
              },
              '&.Mui-focused fieldset': {
                borderColor: '#00796b',
                borderWidth: 2,
              },
              '&.Mui-focused': {
                boxShadow: '0 4px 16px -4px rgba(0, 121, 107, 0.2)',
              },
            },
            '& .MuiInputBase-input': {
              py: 1.5,
              px: 2,
            },
          }}
          inputProps={{
            'aria-label': 'Type your message',
            maxLength: 500,
          }}
        />
        <IconButton
          type="submit"
          disabled={loading || !input.trim()}
          aria-label="Send message"
          sx={{
            width: 48,
            height: 48,
            borderRadius: '14px',
            background: loading || !input.trim() 
              ? 'rgba(0, 121, 107, 0.1)' 
              : 'linear-gradient(135deg, #00796b 0%, #004d40 100%)',
            color: loading || !input.trim() ? 'rgba(0, 121, 107, 0.4)' : 'white',
            boxShadow: loading || !input.trim() 
              ? 'none' 
              : '0 4px 16px -4px rgba(0, 77, 64, 0.4)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            '&:hover': {
              background: loading || !input.trim() 
                ? 'rgba(0, 121, 107, 0.1)' 
                : 'linear-gradient(135deg, #00897b 0%, #00695c 100%)',
              transform: loading || !input.trim() ? 'none' : 'scale(1.05)',
            },
          }}
        >
          {loading ? (
            <CircularProgress size={20} sx={{ color: 'rgba(0, 121, 107, 0.4)' }} />
          ) : (
            <SendIcon sx={{ fontSize: 22 }} />
          )}
        </IconButton>
      </Box>
    </Paper>
  );
};

export default ChatDemo;
